import { Divider, Drawer, Grid, Typography, Checkbox } from '@material-ui/core';
import {
  ConviteUsuarioRequest,
  FuncaoInternaParticipante,
  FuncaoModuloResponse,
  FuncaoParticipante,
  GrupoAcesso,
  Modulos,
  ModuloUsuarioResponse,
  ParticipanteResponse,
  Perfil,
} from 'common/genericTypes';
import { Dropdown, SmSpace, TextField, Button, RadioButton } from 'm2m.components';
import { useEffect, useState } from 'react';
import { perfilOptions, grupoAcessoOptions, Option } from 'common/genericOptions';
import { validaEmail } from 'utils/validation';
import { RootState } from "store";
import { useDispatch, useSelector } from "react-redux";
import ParticipanteAutoComplete from 'components/participante/participanteAutoComplete';
import UploadDocs, { Attachment } from 'components/uploadDocs';
import React from 'react';
import { addAlert } from 'store/modules/snackBar';

interface Props {
  participantes: Array<ParticipanteResponse>;
  open: boolean;
  onClose: () => void;
}

enum TipoUsuario {
  ParticipanteComum = "PARTICIPANTE_COMUM",
  Imf = "IMF"
}

const tipoUsuarioOptions: Option[] = [
  {
    description: 'IMF',
    value: TipoUsuario.Imf
  },
  {
    description: 'Participante Comum',
    value: TipoUsuario.ParticipanteComum
  }
];

type FuncaoType = FuncaoParticipante | FuncaoInternaParticipante;

export const CriarUsuario: React.FC<Props> = (props) => {
  const { participantes, open, onClose } = props;

  const [email, setEmail] = useState<string>('');
  const [nome, setNome] = useState<string>('');
  const [grupoAcesso, setGrupoAcesso] = useState<GrupoAcesso>(GrupoAcesso.CadastroLeitura);

  const [modulos, setModulos] = useState<Array<FuncaoModuloResponse>>([]);

  const [perfis, setPerfis] = useState<Array<{ funcao: FuncaoType, perfil: Perfil }>>([]);

  const [tipoUsuario, setTipoUsuario] = useState<TipoUsuario>(TipoUsuario.Imf);
  const [participante, setParticipante] = useState<ParticipanteResponse | null>(null);
  const [documento, setDocumento] = useState<File | undefined>();

  const isUsuarioGestaoAcessos = useSelector((state: RootState) => state.auth.usuario.grupoAcesso === GrupoAcesso.GestaoAcessos);
  const participanteId = useSelector((state: RootState) => state.auth.usuario.participante.id);
  const getModulosLimiteApi = useSelector((state: RootState) => state.usuarioApi.postModulosLimite);
  const postConvite = useSelector((state: RootState) => state.usuarioApi.postGerarConvite);
  const dispatch = useDispatch();

  const isCadastroValido = () => {
    let isValid = !!(nome && email && validaEmail(email)) && perfis.every(p => !!p.perfil);

    if (!isValid) return false;

    if (isUsuarioGestaoAcessos) {
      if (tipoUsuario === TipoUsuario.Imf) {
        isValid = !!grupoAcesso;
      } else {
        isValid = !!participante && !!documento;
      }
    }

    return isValid;
  }

  const getModulosLimite = async (funcoes: Array<FuncaoType>) => {
    try {
      const result = await getModulosLimiteApi(dispatch, { funcoes: funcoes, habilitado: false }, true);
      setPerfis(funcoes.map(f => ({ funcao: f, perfil: Perfil.Leitura })));
      setModulos(result);
    } catch (error) { }
  };

  const handleUpdateFileToStore = (attachments: Attachment[]): void => {
    const newAttachment = attachments?.[0].file;
    setDocumento(newAttachment);
  };

  const handleSubmit = async () => {
    try {
      const payload: ConviteUsuarioRequest = {
        email,
        nome,
        grupoAcesso: isUsuarioGestaoAcessos && tipoUsuario === TipoUsuario.Imf ? grupoAcesso : GrupoAcesso.Comum,
        participanteId: participante!.id,
        funcaoModulos: perfis.map(p => {
          const funcaoModulos = modulos.find(m => m.funcao === p.funcao)?.modulos ?? [];
          return {
            funcao: p.funcao, perfil: p.perfil,
            modulosUsuario: funcaoModulos.map(fm => ({ id: fm.id, habilitado: fm.habilitado }))
          }
        }),
        documento: documento
      };

      await postConvite(dispatch, payload, true);
      dispatch(addAlert({
        message: 'O convite foi enviado. Quando o convite for aceito o novo usuário deve aparecer na listagem.',
        severity: 'success',
      }));
      onClose();
    } catch (error) { }
  }

  const changeModulos = (moduloId: number, funcao: FuncaoType) => {
    const newModulos = [...modulos];

    const objectToUpdate = newModulos.find((x) => x.funcao === funcao)?.modulos.find(x => x.id === moduloId);

    if (objectToUpdate)
      objectToUpdate.habilitado = !objectToUpdate.habilitado;

    setModulos(newModulos);
  };

  useEffect(() => {
    const newModulos = [...modulos];

    perfis.forEach(p => {
      const objectToUpdate = newModulos.find((x) => x.funcao === p.funcao)?.modulos.find(x => x.nomeEnum === Modulos.GESTAO_ACESSOS);

      if (objectToUpdate)
        objectToUpdate.habilitado = (p.perfil === Perfil.Master && tipoUsuario === TipoUsuario.ParticipanteComum) || grupoAcesso === GrupoAcesso.GestaoAcessos;
    });

    setModulos(newModulos);
  }, [perfis, tipoUsuario, grupoAcesso]);

  useEffect(() => {
    if (tipoUsuario === TipoUsuario.Imf) {
      const newParticipante = participantes.find(p => p.id === participanteId);
      setParticipante(newParticipante ?? null);
      setPerfis([]);
      setGrupoAcesso(GrupoAcesso.CadastroLeitura);
    } else if (isUsuarioGestaoAcessos) {
      setParticipante(null);
      setPerfis([]);
      setGrupoAcesso(GrupoAcesso.Comum);
    }
  }, [tipoUsuario, participanteId, isUsuarioGestaoAcessos]);

  const ListaModulos = (funcao: FuncaoType) => {
    const modulosFuncao = modulos.find(m => m.funcao === funcao);

    if (modulosFuncao && modulosFuncao.modulos.length > 0) {
      return (
        <Grid container>
          <Typography variant="body1">{"Módulos"}</Typography>
          <SmSpace />
          {modulosFuncao.modulos.map((modulo: ModuloUsuarioResponse, index: number) => {
            if (modulo.nomeEnum === Modulos.GESTAO_ACESSOS) {
              return (
                <Grid key={index} item md={12}>
                  <Checkbox color={'primary'} checked={modulo.habilitado} disabled onChange={() => changeModulos(modulo.id, funcao)} />
                  {modulo.nome}
                </Grid>
              );
            }

            return (
              <Grid key={index} item md={12}>
                <Checkbox
                  color={'primary'}
                  checked={modulo.habilitado}
                  onChange={() => {
                    changeModulos(modulo.id, funcao);
                  }}
                />
                {modulo.nome}
              </Grid>
            );
          })}
        </Grid>
      );
    } else
      return null;
  };

  const handlePerfilChange = (funcao: FuncaoType, perfil: Perfil) => {
    let newPerfis = perfis.map(p => {
      return (p.funcao === funcao) ? { ...p, perfil } : p;
    });

    if (!newPerfis.some(np => np.funcao === funcao))
      newPerfis = [...newPerfis, { funcao, perfil }];

    setPerfis(newPerfis);
  }

  const PerfilComponent = (funcao: FuncaoType) => {
    const perfilData = perfis.find(p => p.funcao === funcao);

    return (
      <Dropdown
        label={`Perfil`}
        required
        options={perfilOptions}
        value={perfilData?.perfil ?? ''}
        onChange={(value) => handlePerfilChange(funcao, value as Perfil)}
      />
    );
  }

  useEffect(() => {
    if (!isUsuarioGestaoAcessos) {
      const result = participantes.find(p => p.id === participanteId);
      setParticipante(result ?? null);
      setTipoUsuario(TipoUsuario.ParticipanteComum);
    }
  }, [participanteId, isUsuarioGestaoAcessos, participantes]);

  useEffect(() => {
    if (participante)
      getModulosLimite(participante.funcoes.map(f => f.funcao));
    else
      setModulos([]);
  }, [participante]);

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ style: { padding: '40px', width: 520 } }}>
      <Grid container>
        <Grid item md={12}>
          <Typography variant="h3">{`Novo Usuário`}</Typography>
          <SmSpace />
          <Divider variant="fullWidth" />
          <SmSpace />
          <TextField label="Nome" required value={nome} onChange={setNome} />
          <SmSpace />
          <TextField
            label="E-mail *"
            value={email}
            onChange={setEmail}
            error={!!email && !validaEmail(email)}
            errorMessage={'E-mail inválido'}
          />
          <SmSpace />
          {isUsuarioGestaoAcessos && <>
            <RadioButton
              options={tipoUsuarioOptions}
              value={tipoUsuario}
              onChange={(value: string) => setTipoUsuario(value as TipoUsuario)}
              inline
            />
            <SmSpace />
            {tipoUsuario === TipoUsuario.Imf ?
              <Dropdown
                label={`Grupo de Acesso`}
                required
                options={grupoAcessoOptions.filter((g) => g.value !== GrupoAcesso.Comum)}
                value={grupoAcesso}
                onChange={(value) => setGrupoAcesso(value as GrupoAcesso)}
              /> :
              <ParticipanteAutoComplete
                label={`Participante *`}
                participantes={participantes}
                participante={participante}
                onChange={(value) => setParticipante(value)}
              />
            }
            <SmSpace />
          </>}
          {participante && participante.funcoes.map((funcao, index) => <React.Fragment key={index}>
            <Typography variant='body2'>{`Função: ${funcao.funcao}`}</Typography>
            <SmSpace />
            {PerfilComponent(funcao.funcao)}
            <SmSpace />
            {ListaModulos(funcao.funcao)}
            <SmSpace />
          </React.Fragment>)}
          {isUsuarioGestaoAcessos && tipoUsuario === TipoUsuario.ParticipanteComum &&
            <UploadDocs
              multipleFiles={false}
              inputAttachments={[]}
              onChange={handleUpdateFileToStore}
            />}
          <SmSpace />
          <Button onClick={handleSubmit} variant={'contained'} color={'primary'} fullWidth disabled={!isCadastroValido()}>
            {`Convidar Usuário`}
          </Button>
        </Grid>
      </Grid>
    </Drawer>
  );
};